import { useEffect, useState  } from 'react';
import { useTranslation } from 'react-i18next';

import { Stack, TextField, Autocomplete, } from "@mui/material";

import dayjs from 'dayjs';
import 'dayjs/locale/ru';
import 'dayjs/locale/en';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';

const TIME_ZONES = Intl.supportedValuesOf('timeZone');

function getOffset (timezone, timestamp)
{
	if (!timezone) return '';
	if (timezone.startsWith('UTC')) return timezone.replace(/^UTC/, '');
	const date = timestamp ? dayjs.unix(timestamp).toDate() : new Date();
	return new Intl.DateTimeFormat('en-US', { timeZone:timezone, timeZoneName: 'longOffset', }) .formatToParts(date) .find(p => p.type === 'timeZoneName') ?.value.replace('GMT', '') || '+00:00'
}

function normalizeOffset(value)
{
	const match = value.trim().match(/^(?:UTC)?([+-])(\d{1,2})(?::?(\d{2}))?$/i);
	if (!match) return null;

	const [, sign, hours, minutes = '00'] = match;
	const h = Number(hours);
	const m = Number(minutes);

	if (h > 23 || m > 59) return null;

	return `${sign}${String(h).padStart(2, '0')}:${minutes}`;
}


function getOffsetMinutes (offset)
{
	if (!offset) return 0;
	const normalized=normalizeOffset(offset);
	if (!normalized) return 0;
	const match = normalized.match( /^([+-])(\d{2}):(\d{2})$/);
	if (!match) return 0;

	const [, sign, hours, minutes] = match;
	const total = Number(hours) * 60 + Number(minutes);

	return sign === '+' ? total : -total;
}

export const TimestampTZInput=({ value = { timestamp: null, tz:null, timezone: null }, onChange, label, required = false, fullWidth = true, }) =>
{

const { i18n,t } = useTranslation();
if (!label)  label= t('Дата и время');

if (value.tz=='local') value={...value,tz:null,timezone:Intl.DateTimeFormat().resolvedOptions().timeZone};
if (!value.tz && value.timezone) value.tz=getOffset(value.timezone,value.timestamp);
if (!value.tz) value.tz='';

const [tzInput, setTzInput] = useState( value.tz);
useEffect(() => {
	setTzInput(value.tz);
}, [value.tz]);

const handleDateChange = date =>
{
	onChange({ ...value, timestamp: date ? date.unix() : '', });
};

const getDateValue = () =>
{
	const offsetMinutes = getOffsetMinutes(value.tz);

	return dayjs .unix(value.timestamp) .utcOffset(offsetMinutes);
};

const handleTZSelect = (_, timezone) =>
{
	if (!timezone)
	{
		setTzInput('');
		onChange({ ...value, tz:'', timezone: '', });
		return;
	};

	const tz=getOffset(timezone, value.timestamp);
	setTzInput( tz);

	onChange({ ...value, tz, timezone, });
};

const handleTZInputChange = (_, inputValue, reason) =>
{
	if (reason !== 'input' && reason !== 'clear') { return; };

	setTzInput(inputValue);
	if (!inputValue) { onChange({ ...value, tz:'', timezone: '', }); return; };

	onChange({ ...value, tz: inputValue, });
};

const dateTimeFormat = i18n.language === 'ru' ? 'DD.MM.YYYY HH:mm' : 'MM/DD/YYYY hh:mm A';

const selectedZone = TIME_ZONES.includes(value.timezone) ? value.timezone : null;

return (
<Stack direction="row" spacing={1} width={fullWidth ? '100%' : 'auto'} >

<LocalizationProvider key={i18n.language} dateAdapter={AdapterDayjs} adapterLocale={i18n.language} >
<DateTimePicker
	label={label}
	value={value.timestamp ? dayjs .unix(value.timestamp) .utcOffset(getOffsetMinutes(value.tz)): null }
	onChange={handleDateChange}
	format={dateTimeFormat}
	ampm={i18n.language === 'en'}
	slotProps={{ textField: { required, fullWidth, }, }}
	sx={{ flex: 1 }}
/>
</LocalizationProvider>

<Autocomplete
	freeSolo
	options={TIME_ZONES}
	value={selectedZone}
	inputValue={tzInput}
	onChange={handleTZSelect}
	onInputChange={handleTZInputChange}
	filterOptions={(options, { inputValue }) =>
	{
		const offset = normalizeOffset(inputValue);
		if (offset) return options.filter( timezone => getOffset(timezone,value.timestamp) === offset);
		return options.filter(timezone => timezone.toLowerCase().includes(inputValue.toLowerCase()));
	}}
	sx={{ width: 110 }}
	slotProps={{ popper: { sx: { width: '300px !important', }, }, }}
	renderInput={params => ( <TextField {...params} label={t("Часовой пояс")} placeholder="+00:00" />)}
/>

</Stack>
);
} // TimestampTZInput

